/* eslint-disable promise/param-names,promise/avoid-new,compat/compat,immutable/no-mutation,jsx-control-statements/jsx-jcs-no-undef,max-len */
import patchSvg from 'svg4everybody';
import nprogress from 'nprogress';
import * as Sentry from '@sentry/browser';
import {
  GOOGLE_ANALYTICS_ID,
  SENTRY_CLIENT_DNS,
  ENV_NAME,
  ADMIN_URL,
  INTERCOM_CHAT_APP_ID,
} from './constants/env';
import { setViewportHeight } from './utils/rotationHelper';
import startGoogleAnalytics from './utils/ga';

const htmlNode = document.documentElement;

export function initialLoader() {
  nprogress.configure({ showSpinner: false });

  document.onreadystatechange = () => {
    /*
     * Disabling 'Loading...' state when the document gets the interactive mode
     * */
    if (document.readyState === 'interactive') {
      htmlNode.classList.remove('html--initial');
      document.getElementById('loading').remove();
      document.getElementById('loading-style').remove();

      if (GOOGLE_ANALYTICS_ID) {
        startGoogleAnalytics(GOOGLE_ANALYTICS_ID);
      }
    }
  };
}

export function enableServiceWorker() {
  /*
   * Enable service workers in production mode
   * */
  /* eslint-disable global-require, import/no-extraneous-dependencies */
  const runtime = require('offline-plugin/runtime');

  runtime.install({
    onUpdateReady: () => {
      runtime.applyUpdate();
    },
  });
}

export function initSentry() {
  Sentry.init({
    dsn: SENTRY_CLIENT_DNS,
    environment: ENV_NAME,
  });
}

export function windowIntlPolyfill() {
  return new Promise((resolve, reject) => {
    if (!window.Intl) {
      new Promise(_resolve => {
        _resolve(require('intl'));
      })
        .then(() =>
          Promise.all([
            require('intl/locale-data/jsonp/en.js'),
            require('intl/locale-data/jsonp/es.js'),
          ]),
        )
        .then(() => resolve())
        .catch(err => {
          reject(err);
          throw err;
        });
    } else {
      resolve();
    }
  });
}

/*
 * Loading contents of SVG as a string to inject it into the `#svg` in the <body> element and
 * make SVG available inlined, not like an external resource. SVG MUST be cleaned up before
 * to prevent unnecessary bandwidth losses and XSS
 * */
export function loadSVG() {
  require.ensure(
    [],
    () => {
      /* eslint-disable global-require, import/no-unresolved, import/no-webpack-loader-syntax,
     import/no-extraneous-dependencies */
      document.getElementById(
        'svg',
      ).innerHTML = require('raw-loader!./assets/symbol/icons.svg');
      patchSvg({
        polyfill: true,
      });
      /* eslint-enable global-require, import/no-unresolved, import/no-webpack-loader-syntax,
     import/no-extraneous-dependencies */
    },
    'svg-icons',
  );
}

/*
 * Async font loading and caching in localStorage
 * https://github.com/bdadam/OptimizedWebfontLoading
 * Described here: http://bdadam.com/blog/loading-webfonts-with-high-performance.html
 * */
export function loadFonts() {
  require.ensure(
    [],
    () => {
      /* eslint-disable global-require */
      const { FONT_FAMILY } = require('./constants/styles');
      const loadFont = require('./libs/fontloader');
      const woff = require('./assets/nunitoSans.woff.css');
      const woff2 = require('./assets/nunitoSans.woff2.css');
      /* eslint-enable global-require */
      loadFont(FONT_FAMILY.primary, woff, woff2);
    },
    'fontloader',
  );
}

/*
 * Device type detecting to add specific class name to the <html> element. It helps to omit double
 * tap issue at touch devices, when the hover styles applies at the first tap and the actions
 * triggers at the second. See `handheld-hover` Stylus mixin for details
 * */
export function deviceTypeDetecting() {
  require.ensure(
    [],
    () => {
      /* eslint-disable global-require */
      const MobileDetect = require('mobile-detect');
      /* eslint-enable global-require */
      const md = new MobileDetect(window.navigator.userAgent);
      const deviceTypeClassName = md.mobile()
        ? 'html--handheld'
        : 'html--notHandheld';
      htmlNode.classList.add(deviceTypeClassName);

      // iPhone 6s+ landscape mode handling
      if (md.is('iPhone') && md.userAgent() === 'Safari') {
        const supportsOrientationChange = 'onorientationchange' in window;
        const orientationEvent = supportsOrientationChange
          ? 'orientationchange'
          : 'resize';

        // initial landscape orientation
        if (window.innerHeight < window.innerWidth) setViewportHeight();
        // update on orientation change
        window.addEventListener(orientationEvent, setViewportHeight, false);
      }
    },
    'mobile-detect',
  );
}

export function redirectToAdminWebsite() {
  if (window.location.pathname === '/admin') {
    window.location.replace(ADMIN_URL);
  }
}

export function addIntercomIntegration() {
  const APP_ID = INTERCOM_CHAT_APP_ID;

  (function() {
    const w = window;
    const ic = w.Intercom;
    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      const d = document;
      const i = function() {
        i.c(arguments);
      };
      i.q = [];
      i.c = function(args) {
        i.q.push(args);
      };
      w.Intercom = i;
      const l = function() {
        const s = d.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = 'https://widget.intercom.io/widget/' + APP_ID;
        const x = d.getElementsByTagName('script')[0];
        x.parentNode.insertBefore(s, x);
      };
      if (document.readyState === 'complete') {
        l();
      } else if (w.attachEvent) {
        w.attachEvent('onload', l);
      } else {
        w.addEventListener('load', l, false);
      }
    }
  })();

  window.Intercom('boot', {
    api_base: 'https://api-iam.intercom.io',
    app_id: APP_ID,
  });
}



// WEBPACK FOOTER //
// ./src/bootstrapUtils.js